import { useEffect, useRef } from "react";
import authConfig from "../auth.config";
import type { RefreshMethod } from "../types";
import { debugLog } from "../utils/debug";
import { tokenManager } from "../utils/token-manager";

export function useProactiveTokenRefresh(
	isAuthenticated: boolean,
	refresh: RefreshMethod,
) {
	const hasBeenTriggered = useRef(false);

	useEffect(() => {
		if (!isAuthenticated) return;

		if (hasBeenTriggered.current) return;
		hasBeenTriggered.current = true;

		const checkAndRefreshToken = () => {
			// Only refresh if this tab is visible/active
			if (
				typeof document !== "undefined" &&
				document.visibilityState === "hidden"
			) {
				return;
			}

			const token = tokenManager.getAccessToken();

			if (!token) return;

			const expiration = tokenManager.getTokenExpiration();

			if (!expiration) return;

			const now = Date.now();
			const secondsFromNow = now + authConfig.checkTokenExpirationThreshold;

			// If token expires within threshold, refresh it proactively
			if (secondsFromNow >= expiration) {
				debugLog("Token expires soon, triggering proactive refresh", {
					expiresIn: expiration - now,
					threshold: authConfig.checkTokenExpirationThreshold,
				});
				// Refresh token directly (cookie storage handles cross-tab sharing)
				refresh().catch((error: Error) => {
					debugLog("Proactive token refresh failed", error);
				});
			}
		};

		// Set up interval for regular checks
		const interval = setInterval(() => {
			checkAndRefreshToken();
		}, authConfig.checkTokenExpirationInterval);

		// Also check when tab becomes visible (in case it was hidden when refresh was needed)
		const handleVisibilityChange = () => {
			if (
				typeof document !== "undefined" &&
				document.visibilityState === "visible"
			) {
				// Small delay to ensure tab is fully active
				setTimeout(checkAndRefreshToken, authConfig.tabVisibilityCheckDelay);
			}
		};

		if (typeof document !== "undefined") {
			document.addEventListener("visibilitychange", handleVisibilityChange);
		}

		return () => {
			clearInterval(interval);
			if (typeof document !== "undefined") {
				document.removeEventListener(
					"visibilitychange",
					handleVisibilityChange,
				);
			}
		};
	}, [isAuthenticated, refresh]);
}
